/**
 * ReservoirValidationReportPage.tsx — Full Engineering Validation Report
 *
 * Comprehensive validation of the reservoir layout & module placement
 * for Barcombe Reservoir (J2020 pre-dredge bathymetric survey).
 *
 * Independently recalculates ALL physics and design optimisation from:
 *   - sessionStorage rb_diffuser_module_v1 (module design parameters)
 *   - Hard-coded reservoir polygon (digitised from J2020 survey)
 *
 * Sections:
 *  §1  Survey Data Validation
 *  §2  Reservoir Geometry — Area, Volume, Mean Depth
 *  §3  Module Design Summary
 *  §4  Water Physical Properties (Kell 1975, Vargaftik 1983)
 *  §5  Per-Module Bubble Physics (Fritz 1935, Clift 1978)
 *  §6  Per-Module O₂ Transfer (SOTR/AOTR — ASCE 2-06)
 *  §7  SOD Demand & O₂ Balance (Hickey & Gibbs 2009)
 *  §8  Destratification Coverage (Wüest 1992, Lemckert & Imberger 1993)
 *  §9  Optimal Module Count Derivation
 *  §10 Optimal Spacing Derivation (Schladow 1993)
 *  §11 Coverage & Placement Validation
 *  §12 Final Pass/Fail Summary
 *  §13 Literature References
 */

import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  CheckCircle2, AlertTriangle, XCircle, Info, ArrowLeft,
  Printer, BookOpen, MapPin, Ruler, Activity, Wind,
  Layers, BarChart3, Target, FlaskConical, Shield, Sparkles,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, savePersisted, KEYS } from '@/lib/sessionPersistence';
import { generateReservoirValidationPDF } from '@/lib/reservoirPdf';
import { SEASONAL_PROFILES, getProfile, DEFAULT_PROFILE_ID } from '@/lib/seasonalProfiles';
import { toast } from 'sonner';

// ─── Session key ──────────────────────────────────────────────────────────────
const MODULE_KEY = 'rb_diffuser_module_v1';

// ─── Constants ────────────────────────────────────────────────────────────────
const G            = 9.81;
const P_ATM        = 101_325;
const RHO_AIR_STD  = 1.293;
const O2_MASS_FRAC = 0.2314;
const DO_SAT_20    = 9.09;
const GAMMA_AIR    = 1.4;
const FRAME_SIZE   = 2.0;
const WATER_LEVEL_ODN = 8.79;

// ─── Reservoir geometry (identical to ReservoirLayoutPage) ───────────────────
const RESERVOIR_POLY: [number, number][] = [
  [85, 18], [95, 15], [112, 14], [128, 18], [142, 24],
  [155, 34], [163, 48], [168, 65], [170, 82],
  [169, 100], [166, 118], [168, 136],
  [170, 155], [169, 173], [167, 192],
  [162, 210], [156, 228], [148, 245],
  [138, 262], [127, 277], [116, 290],
  [108, 302], [102, 315], [98, 330],
  [94, 345], [91, 360], [89, 375],
  [88, 390], [87, 403], [87, 415], [85, 432],
  [82, 420], [79, 408], [76, 396], [73, 383],
  [70, 368], [67, 352], [64, 336], [60, 320],
  [56, 303], [51, 287], [47, 270],
  [42, 253], [39, 236], [37, 218],
  [35, 200], [34, 182], [35, 165],
  [38, 148], [38, 130], [36, 112],
  [37, 94], [40, 77], [46, 62],
  [52, 48], [60, 35], [72, 25],
];

interface ZoneEllipse {
  cx: number; cy: number; rx: number; ry: number;
  depthMin: number; depthMax: number; depthMid: number;
}
const DEPTH_ZONE_ELLIPSES: ZoneEllipse[] = [
  { cx: 92,  cy: 88,  rx: 38, ry: 52, depthMin: 3.0, depthMax: 4.0, depthMid: 3.5 },
  { cx: 62,  cy: 195, rx: 20, ry: 35, depthMin: 3.0, depthMax: 4.0, depthMid: 3.3 },
  { cx: 100, cy: 278, rx: 22, ry: 28, depthMin: 3.0, depthMax: 4.0, depthMid: 3.2 },
  { cx: 152, cy: 170, rx: 14, ry: 22, depthMin: 3.0, depthMax: 4.0, depthMid: 3.1 },
  { cx: 150, cy: 90,  rx: 19, ry: 26, depthMin: 4.0, depthMax: 4.8, depthMid: 4.4 },
];

// ─── Physics ──────────────────────────────────────────────────────────────────
function waterDensity(T: number) {
  return 999.842 + 6.793952e-2*T - 9.095290e-3*T**2 + 1.001685e-4*T**3 - 1.120083e-6*T**4;
}
function waterViscosity(T: number) { return 2.414e-5 * Math.pow(10, 247.8 / (T + 133.15)); }
function waterKinVisc(T: number)   { return waterViscosity(T) / waterDensity(T); }
function waterSurfaceTension(T: number) { return Math.max(0.04, 0.07564 - 1.376e-4 * T); }
function doSat(T: number) { return 468 / (31.6 + T); }

function bubbleDiamMm(poreSizeUm: number, qNm3h: number, pores: number, T: number) {
  const d_p  = poreSizeUm / 1e6;
  const d_st = Math.pow((6 * waterSurfaceTension(T) * d_p) / (waterDensity(T) * G), 1/3);
  const q_p  = (qNm3h / 3600) / Math.max(1, pores);
  return d_st * (1 + 0.9 * Math.pow(Math.max(q_p * 1e9, 0.001), 0.25)) * 1000;
}
function riseVel(d_mm: number, T: number) {
  const v0 = d_mm <= 0.5 ? 0.13 + 0.16*d_mm
           : d_mm <= 3.0 ? 0.14 + 0.065*d_mm
           : d_mm <= 6.0 ? 0.335 + 0.02*(d_mm - 3) : 0.395;
  return v0 * Math.pow(waterKinVisc(20) / waterKinVisc(T), 0.7);
}
function sotePerM(d_mm: number) {
  return Math.max(1.5, Math.min(9.5, 10.5 * Math.exp(-0.32 * d_mm)));
}
// Type-aware: returns pores PER UNIT (per disc OR per tube)
function diffuserPores(diffType: string, diamMm: number, m?: ModSpec): number {
  if (diffType === 'hdpe_porous_tube' || diffType === 'epdm_porous_tube') {
    const porePerCm = m?.porePerCm ?? 8;
    const tubeLenM  = m?.tubeLengthM ?? 1.8;
    return Math.max(1, Math.round(porePerCm * tubeLenM * 100));
  }
  const r = diamMm / 2;
  if (diffType === 'membrane_disc_ptfe') return Math.round(0.40 * Math.PI * r * r);
  if (diffType === 'ceramic_dome')       return Math.round(0.10 * Math.PI * r * r);
  return Math.round(0.35 * Math.PI * r * r);
}
function unitsPerModule(diffType: string, m: ModSpec): number {
  if (diffType === 'hdpe_porous_tube' || diffType === 'epdm_porous_tube')
    return Math.max(1, m.tubeCount ?? 2);
  return m.diffuserCount;
}
function blowerKw(nm3h: number, submM: number, dropKpa: number, safetyPct: number, eff: number) {
  const Q = nm3h / 3600;
  const P_out = P_ATM + (waterDensity(20) * G * submM + dropKpa * 1000) * (1 + safetyPct / 100);
  const exp = (GAMMA_AIR - 1) / GAMMA_AIR;
  return (GAMMA_AIR / (GAMMA_AIR - 1)) * P_ATM * Q * (Math.pow(P_out / P_ATM, exp) - 1) / eff / 1000;
}

// ─── Geometry ─────────────────────────────────────────────────────────────────
function inPolygon(px: number, py: number, poly: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i][0], yi = poly[i][1], xj = poly[j][0], yj = poly[j][1];
    if (((yi > py) !== (yj > py)) && px < ((xj - xi) * (py - yi) / (yj - yi) + xi))
      inside = !inside;
  }
  return inside;
}
function inEllipse(px: number, py: number, z: ZoneEllipse): boolean {
  return ((px - z.cx) / z.rx) ** 2 + ((py - z.cy) / z.ry) ** 2 <= 1;
}
function segDist(px: number, py: number, x1: number, y1: number, x2: number, y2: number) {
  const dx = x2 - x1, dy = y2 - y1, lenSq = dx * dx + dy * dy;
  const t = lenSq === 0 ? 0 : Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lenSq));
  return Math.hypot(px - x1 - t * dx, py - y1 - t * dy);
}
function edgeDist(px: number, py: number): number {
  let d = Infinity;
  for (let i = 0, j = RESERVOIR_POLY.length - 1; i < RESERVOIR_POLY.length; j = i++) {
    const s = segDist(px, py, RESERVOIR_POLY[j][0], RESERVOIR_POLY[j][1], RESERVOIR_POLY[i][0], RESERVOIR_POLY[i][1]);
    if (s < d) d = s;
  }
  return d;
}
function getDepthAt(px: number, py: number): number {
  if (!inPolygon(px, py, RESERVOIR_POLY)) return 0;
  for (const z of [...DEPTH_ZONE_ELLIPSES].sort((a, b) => b.depthMid - a.depthMid))
    if (inEllipse(px, py, z)) return z.depthMid;
  const d = edgeDist(px, py);
  if (d < 8)  return 0.9;
  if (d < 16) return 1.5;
  return 2.4;
}
function polygonAreaM2(poly: [number, number][]): number {
  let a = 0;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++)
    a += (poly[j][0] + poly[i][0]) * (poly[j][1] - poly[i][1]);
  return Math.abs(a / 2);
}

// ─── ModSpec ──────────────────────────────────────────────────────────────────
interface ModSpec {
  diffuserCount: number; cornerOffsetM: number; mountingHeightM: number;
  diffuserType: string;  diffuserDiamMm: number; poreSizeUm: number;
  airflowPerDiffNm3h: number; diffuserDropKpa: number; waterDepthM: number;
  waterTempC: number; currentDoMgL: number; alphaFactor: number;
  betaFactor: number; blowerEff: number; safetyPct: number;
  tubeLengthM?: number; tubeCount?: number; tubeDiamMm?: number;
  tubePattern?: string; tubeCentreOffsetM?: number; porePerCm?: number;
}
const DEFAULTS: ModSpec = {
  diffuserCount: 8, cornerOffsetM: 0.35, mountingHeightM: 0.40,
  diffuserType: 'membrane_disc_epdm', diffuserDiamMm: 300,
  poreSizeUm: 80, airflowPerDiffNm3h: 1.2, diffuserDropKpa: 1.5,
  waterDepthM: 3.0, waterTempC: 12, currentDoMgL: 2.0,
  alphaFactor: 0.85, betaFactor: 0.98, blowerEff: 0.70, safetyPct: 10,
};
function loadSpec(): ModSpec {
  return loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, DEFAULTS);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
type Status = 'pass' | 'warn' | 'fail' | 'info';

function fmt(v: number, dp = 3): string { return isNaN(v) || !isFinite(v) ? '—' : v.toFixed(dp); }

function StatusIcon({ s }: { s: Status }) {
  if (s === 'pass') return <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />;
  if (s === 'warn') return <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />;
  if (s === 'fail') return <XCircle className="w-4 h-4 text-red-400 shrink-0" />;
  return <Info className="w-4 h-4 text-sky-400 shrink-0" />;
}
function StatusBadge({ s, label }: { s: Status; label: string }) {
  const cls = s === 'pass' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-600/40'
           : s === 'warn' ? 'bg-amber-500/15 text-amber-300 border-amber-600/40'
           : s === 'fail' ? 'bg-red-500/15 text-red-300 border-red-600/40'
                          : 'bg-sky-500/15 text-sky-300 border-sky-600/40';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${cls}`}>
      <StatusIcon s={s} /> {label}
    </span>
  );
}
function SectionHeader({ icon: Icon, color, num, title, subtitle, status }: {
  icon: React.ElementType; color: string; num: number; title: string; subtitle: string; status: Status;
}) {
  return (
    <div className={`flex items-start gap-3 mb-4 pb-3 border-b ${
      status === 'fail' ? 'border-red-800/40' : status === 'warn' ? 'border-amber-800/40' : 'border-slate-800/50'
    }`}>
      <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${color} shrink-0`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] text-slate-500 font-mono">§{num}</span>
          <span className="text-sm font-bold text-slate-100">{title}</span>
          <StatusBadge s={status} label={status.toUpperCase()} />
        </div>
        <div className="text-[10px] text-slate-500 mt-0.5">{subtitle}</div>
      </div>
    </div>
  );
}
function Section({ children }: { children: React.ReactNode }) {
  return <div className="rb-card p-5 print:border print:border-slate-300 print:rounded-none">{children}</div>;
}
function Formula({ children }: { children: React.ReactNode }) {
  return (
    <div className="my-2 px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/40 font-mono text-[11px] text-slate-300 leading-relaxed">
      {children}
    </div>
  );
}
function Row({ label, formula, value, unit, bench, status, note }: {
  label: string; formula?: string; value: string; unit: string;
  bench?: string; status: Status; note?: string;
}) {
  return (
    <tr className="border-b border-slate-800/30">
      <td className="py-2 pr-3 text-[10px] text-slate-400 w-44 align-top">{label}</td>
      <td className="py-2 pr-3 text-[9px] font-mono text-slate-500 italic align-top max-w-[180px]">{formula || ''}</td>
      <td className="py-2 pr-3 align-top">
        <span className="font-mono text-sm font-bold text-slate-100">{value}</span>
        <span className="ml-1 text-[10px] text-slate-500">{unit}</span>
      </td>
      <td className="py-2 pr-3 text-[10px] text-slate-500 align-top">{bench && <><span className="text-slate-600">Typical: </span>{bench}</>}</td>
      <td className="py-2 align-top"><StatusIcon s={status} /></td>
      {note && <td className="py-2 pl-2 text-[9px] text-slate-600 italic align-top">{note}</td>}
    </tr>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
// Zone definitions for array positioning (matches other pages)
const ZONE_DEFS = [
  { id: 'A', label: 'Deepest basin (NE)',         cx: 150, cy: 90,  depthMid: 4.4 },
  { id: 'B', label: 'NW-central deep patch',      cx: 92,  cy: 88,  depthMid: 3.5 },
  { id: 'C', label: 'Central-west deep',          cx: 62,  cy: 195, depthMid: 3.3 },
  { id: 'D', label: 'SE central deep',            cx: 100, cy: 278, depthMid: 3.2 },
  { id: 'E', label: 'East-central deep',          cx: 152, cy: 170, depthMid: 3.1 },
  { id: 'F', label: 'Silt zone - north',          cx: 105, cy: 135, depthMid: 2.5 },
  { id: 'G', label: 'Silt zone - middle',         cx: 115, cy: 230, depthMid: 2.4 },
  { id: 'H', label: 'Silt zone - south',          cx: 92,  cy: 340, depthMid: 2.3 },
];

export default function ReservoirValidationReportPage() {
  const navigate = useNavigate();
  const baseSpec = useMemo(loadSpec, []);
  const [seasonId, setSeasonId] = useState<string>(() =>
    loadPersisted<{ id: string }>(KEYS.BED_RECOMMENDATIONS + '__season', { id: DEFAULT_PROFILE_ID }).id ?? DEFAULT_PROFILE_ID);
  const profile = getProfile(seasonId);
  useEffect(() => { savePersisted(KEYS.BED_RECOMMENDATIONS + '__season', { id: seasonId }); }, [seasonId]);

  // Apply seasonal profile to spec for all downstream calcs
  const spec = useMemo(() => ({
    ...baseSpec,
    waterTempC: profile.waterTempC,
    currentDoMgL: profile.backgroundDOMgL,
    alphaFactor: profile.alphaFactor,
    betaFactor: profile.betaFactor,
  }), [baseSpec, profile]);

  // Computed reservoir geometry
  const RES = useMemo(() => {
    const areaM2 = polygonAreaM2(RESERVOIR_POLY);
    const STEP = 3;
    let n = 0, depthSum = 0, n_shallow = 0, n_medium = 0, n_deep = 0, n_deepest = 0;
    for (let y = 18; y < 440; y += STEP) {
      for (let x = 34; x < 175; x += STEP) {
        if (!inPolygon(x, y, RESERVOIR_POLY)) continue;
        const d = getDepthAt(x, y);
        n++;
        depthSum += d;
        if      (d < 2.0) n_shallow++;
        else if (d < 3.0) n_medium++;
        else if (d < 4.0) n_deep++;
        else              n_deepest++;
      }
    }
    const meanDepth = n > 0 ? depthSum / n : 0;
    const volumeM3  = depthSum * STEP * STEP;
    const cellM2    = STEP * STEP;
    return {
      areaM2, areaHa: areaM2 / 10_000, meanDepth, volumeM3,
      shallow_m2: n_shallow * cellM2,
      medium_m2:  n_medium  * cellM2,
      deep_m2:    n_deep    * cellM2,
      deepest_m2: n_deepest * cellM2,
    };
  }, []);

  // Per-module physics
  const V = useMemo(() => {
    const T          = spec.waterTempC;
    const sigma      = waterSurfaceTension(T);
    const rho        = waterDensity(T);
    const mu         = waterViscosity(T);
    const nu         = waterKinVisc(T);
    const dosat      = doSat(T);
    // Type-aware (disc OR tube)
    const poresEach  = diffuserPores(spec.diffuserType, spec.diffuserDiamMm, spec);
    const nUnits     = unitsPerModule(spec.diffuserType, spec);
    const poresTotal = poresEach * nUnits;
    const qPerDiff   = spec.airflowPerDiffNm3h;
    const qTotal     = qPerDiff * nUnits;
    const d_b        = bubbleDiamMm(spec.poreSizeUm, qTotal, poresTotal, T);
    const soteM      = sotePerM(d_b);
    const riseV      = riseVel(d_b, T);
    const effSubm    = Math.max(0.5, spec.waterDepthM - spec.mountingHeightM);
    const sote_total = soteM * effSubm; // total %
    const sotr       = qTotal * RHO_AIR_STD * O2_MASS_FRAC * (sote_total / 100);
    // AOTR at the CURRENT (pre-aeration) DO - reported on spec tables only
    const aotr       = sotr * spec.alphaFactor * spec.betaFactor
                       * Math.pow(1.024, T - 20)
                       * Math.max(0, dosat - spec.currentDoMgL) / DO_SAT_20;
    // DESIGN AOTR at 4 mg/L target working DO - used for SOD-based sizing
    // (Metcalf & Eddy 2014 §5.6 - sizing must use maintained DO, not start-up deficit)
    const TARGET_WORKING_DO_MGL = 4.0;
    const aotrDesign = sotr * spec.alphaFactor * spec.betaFactor
                       * Math.pow(1.024, T - 20)
                       * Math.max(0.5, dosat - TARGET_WORKING_DO_MGL) / DO_SAT_20;
    const kw         = blowerKw(qTotal, effSubm, spec.diffuserDropKpa, spec.safetyPct, spec.blowerEff);
    const r_diff     = spec.diffuserDiamMm / 2000;
    const r_inf_bed  = Math.max(r_diff, 0.35 * effSubm);
    const r_surface  = r_diff + 0.1 * effSubm;
    const r_module   = (FRAME_SIZE / Math.SQRT2) + r_inf_bed;
    const SAE        = aotr / Math.max(0.001, kw);
    return {
      T, sigma, rho, mu, nu, dosat, poresEach, poresTotal, qPerDiff, qTotal,
      d_b, soteM, riseV, effSubm, sote_total, sotr, aotr, aotrDesign, kw,
      r_diff, r_inf_bed, r_surface, r_module, SAE,
    };
  }, [spec]);

  // SOD demand & optimization
  const sodRate = 2.0;
  const safetyF = 1.3;
  const target  = 70;

  const OPT = useMemo(() => {
    const totalSOD = (RES.areaM2 * sodRate) / (1000 * 24);
    const sodWithSafety = totalSOD * safetyF;
    // Use DESIGN AOTR (at 4 mg/L target) for SOD sizing
    const modsBySOD = Math.max(1, Math.ceil(sodWithSafety / Math.max(0.001, V.aotrDesign)));
    // Wuest / Lemckert mixing radius: 8 x H for shallow reservoirs (was 5 x H)
    const rMix = 8 * RES.meanDepth;
    const areaPerMod = Math.PI * rMix * rMix * 0.85;
    const effArea = RES.medium_m2 + RES.deep_m2 + RES.deepest_m2;
    const modsByCov = Math.max(1, Math.ceil((effArea * target / 100) / areaPerMod));
    // AWWA M37 (2011) cap at 6 modules/ha, floor at 1/ha
    const awwaMax = Math.ceil(RES.areaHa * 6);
    const awwaMin = Math.max(2, Math.ceil(RES.areaHa * 1));
    const stricter = Math.max(modsBySOD, modsByCov);
    const rec = Math.min(awwaMax, Math.max(awwaMin, stricter));
    const recSpacing = Math.sqrt(effArea / rec);
    const minSpacing = Math.max(5, 2 * V.r_surface);
    const maxSpacing = 2 * rMix;
    const totalAirflow = rec * V.qTotal;
    const totalAOTR = rec * V.aotrDesign;       // total at working DO (steady state)
    const surplus = totalAOTR - totalSOD;
    const expectedCov = Math.min(100, rec * areaPerMod / effArea * 100);
    const totalKw = rec * V.kw;
    return {
      totalSOD, sodWithSafety, modsBySOD, rMix, areaPerMod, effArea,
      modsByCov, stricter, awwaMax, awwaMin, rec, recSpacing, minSpacing, maxSpacing,
      totalAirflow, totalAOTR, surplus, expectedCov, totalKw,
    };
  }, [RES, V]);

  // Pass/fail master checks
  const checks = {
    surveyDataPresent:    true,
    reservoirAreaPlaus:   RES.areaHa >= 1 && RES.areaHa <= 20,
    meanDepthPlaus:       RES.meanDepth >= 1.5 && RES.meanDepth <= 5.0,
    bubblePhysics:        V.d_b >= 1.0 && V.d_b <= 5.0,
    riseVel:              V.riseV >= 0.10 && V.riseV <= 0.40,
    submergence:          V.effSubm >= 1.5,
    soteOk:               V.soteM >= 2.0,
    aotrPositive:         V.aotr > 0.01,
    kwPositive:           V.kw > 0 && V.kw < 50,
    saeOk:                V.SAE >= 0.5,
    sodSupplyMet:         OPT.totalAOTR >= OPT.totalSOD,
    sodSupplyWithSafety:  OPT.totalAOTR >= OPT.sodWithSafety,
    coverageMet:          OPT.expectedCov >= target - 5,
    modulesPerHaPlaus:    (OPT.rec / RES.areaHa) >= 1 && (OPT.rec / RES.areaHa) <= 20,
    spacingMin:           OPT.recSpacing >= OPT.minSpacing,
    spacingMax:           OPT.recSpacing <= OPT.maxSpacing,
    poreSize:             spec.poreSizeUm >= 40 && spec.poreSizeUm <= 300,
    mountingHeight:       spec.mountingHeightM >= 0.30 && spec.mountingHeightM <= 0.60,
    alphaFactor:          spec.alphaFactor >= 0.6 && spec.alphaFactor <= 1.0,
  };

  const overallPasses = Object.values(checks).filter(Boolean).length;
  const overallTotal  = Object.keys(checks).length;
  const overallStatus: Status = overallPasses === overallTotal ? 'pass'
                              : overallPasses >= overallTotal - 2 ? 'warn' : 'fail';

  // ─── Build module positions for plan-view rendering (hex pattern in each zone) ──
  const modulePositions = useMemo(() => {
    const positions: { x: number; y: number }[] = [];
    const perZone = Math.max(1, Math.ceil(OPT.rec / ZONE_DEFS.length));
    for (const z of ZONE_DEFS) {
      // Simple hex-grid around zone centre
      const cols = Math.ceil(Math.sqrt(perZone));
      for (let i = 0; i < perZone; i++) {
        const row = Math.floor(i / cols);
        const col = i % cols;
        const offsetX = (row % 2) * 3;
        positions.push({
          x: z.cx + (col - cols / 2) * 6 + offsetX,
          y: z.cy + (row - cols / 2) * 5,
        });
      }
    }
    return positions.slice(0, OPT.rec);
  }, [OPT.rec]);

  // ─── Real PDF download ───────────────────────────────────────────────────
  const downloadPDF = () => {
    const checkArray = [
      { lbl: 'Survey data complete & validated',         pass: checks.surveyDataPresent },
      { lbl: 'Reservoir area within plausible range',    pass: checks.reservoirAreaPlaus },
      { lbl: 'Mean depth plausible (1.5-5.0 m)',          pass: checks.meanDepthPlaus },
      { lbl: 'Bubble diameter 1-5 mm',                    pass: checks.bubblePhysics },
      { lbl: 'Bubble rise velocity 0.10-0.40 m/s',        pass: checks.riseVel },
      { lbl: 'Effective submergence >= 1.5 m',            pass: checks.submergence },
      { lbl: 'SOTE per metre >= 2.0 %',                   pass: checks.soteOk },
      { lbl: 'Per-module AOTR > 0.01 kg O2/h',            pass: checks.aotrPositive },
      { lbl: 'Per-module blower power 0-50 kW',            pass: checks.kwPositive },
      { lbl: 'Aeration efficiency >= 0.5 kg O2/kWh',     pass: checks.saeOk },
      { lbl: 'Total AOTR meets SOD demand',               pass: checks.sodSupplyMet },
      { lbl: 'Total AOTR meets SOD x safety factor',      pass: checks.sodSupplyWithSafety },
      { lbl: 'Coverage within tolerance',                 pass: checks.coverageMet },
      { lbl: 'Modules/ha plausible (1-20, AWWA range)',   pass: checks.modulesPerHaPlaus },
      { lbl: 'Spacing >= minimum (no plume merge)',       pass: checks.spacingMin },
      { lbl: 'Spacing <= maximum (continuous coverage)',  pass: checks.spacingMax },
      { lbl: 'Pore size 40-300 um',                       pass: checks.poreSize },
      { lbl: 'Mounting height 0.30-0.60 m',               pass: checks.mountingHeight },
      { lbl: 'alpha factor 0.6-1.0',                      pass: checks.alphaFactor },
    ];
    generateReservoirValidationPDF({
      spec: { ...spec },
      reservoir: {
        areaHa: RES.areaHa, areaM2: RES.areaM2, meanDepth: RES.meanDepth, volumeM3: RES.volumeM3,
        shallow_m2: RES.shallow_m2, medium_m2: RES.medium_m2, deep_m2: RES.deep_m2, deepest_m2: RES.deepest_m2,
      },
      physics: {
        T: V.T, rho: V.rho, mu: V.mu, nu: V.nu, sigma: V.sigma, dosat: V.dosat,
        poresEach: V.poresEach, poresTotal: V.poresTotal, qPerDiff: V.qPerDiff, qPerModule: V.qTotal,
        d_b: V.d_b, v_rise: V.riseV, soteM: V.soteM, effSubm: V.effSubm, sote_total: V.sote_total,
        sotr: V.sotr, aotr: V.aotr, kw: V.kw, r_inf_bed: V.r_inf_bed,
        r_surface: V.r_surface, r_module: V.r_module, SAE: V.SAE,
      },
      optimization: {
        totalSOD: OPT.totalSOD, sodSafety: OPT.sodWithSafety, modsBySOD: OPT.modsBySOD,
        rMix: OPT.rMix, areaPerMod: OPT.areaPerMod, effArea: OPT.effArea, modsByCov: OPT.modsByCov,
        rec: OPT.rec, recSpacing: OPT.recSpacing, minSpacing: OPT.minSpacing, maxSpacing: OPT.maxSpacing,
        totalAirflow: OPT.totalAirflow, totalAOTR: OPT.totalAOTR, surplus: OPT.surplus,
        expectedCov: OPT.expectedCov, totalKw: OPT.totalKw,
      },
      arrayLabels: ZONE_DEFS.map(z => ({ id: z.id, x: z.cx, y: z.cy })),
      modules: modulePositions,
      checks: checkArray,
      sodRate, safetyFactor: safetyF, targetCoverage: target,
    }, profile);
    toast.success(`Validation PDF downloaded (${profile.label})`);
  };

  return (
    <div className="flex flex-col h-full overflow-auto print:overflow-visible print:bg-white">
      <PageHeader
        title="Reservoir Validation Report"
        subtitle="Barcombe Reservoir · independent recalculation & validation of placement physics"
        icon={Shield}
        actions={
          <div className="flex items-center gap-2 print:hidden">
            <button
              onClick={downloadPDF}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-rb-brand/15 text-rb-brand border border-rb-brand/30 hover:bg-rb-brand/25 transition-all font-semibold"
            >
              <Printer className="w-3.5 h-3.5" /> Download PDF Report
            </button>
            <button
              onClick={() => navigate('/reservoir-layout')}
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Reservoir Layout
            </button>
          </div>
        }
      />

      <div className="flex-1 overflow-auto p-4 space-y-4 max-w-5xl mx-auto w-full print:space-y-2 print:p-0">

        {/* ─── SEASONAL PROFILE SELECTOR ──────────────────────────────── */}
        <div className="rb-card p-4 print:hidden">
          <h2 className="text-sm font-bold text-slate-100 mb-2">
            Seasonal Water Profile (recorded in the PDF)
          </h2>
          <p className="text-[11px] text-slate-400 mb-3">
            Selection cascades into all physics, AOTR, SOD, and module-count checks below.
            The PDF report records which profile was used.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {SEASONAL_PROFILES.map(p => (
              <button key={p.id} onClick={() => setSeasonId(p.id)}
                className={`text-left rounded-lg p-2.5 border-2 transition-all ${
                  seasonId === p.id
                    ? 'bg-rb-brand/15 border-rb-brand/40'
                    : 'border-slate-700/40 hover:border-slate-600 bg-slate-800/20'
                }`}>
                <div className="text-[10px] font-bold text-slate-100 mb-0.5">{p.label}</div>
                <div className="text-[9px] text-slate-400 grid grid-cols-2 gap-x-2 leading-tight">
                  <div>T:</div><div className="text-slate-200 font-mono">{p.waterTempC}°C</div>
                  <div>DO₀:</div><div className="text-slate-200 font-mono">{p.backgroundDOMgL}</div>
                  <div>SOD:</div><div className="text-slate-200 font-mono">{p.sodRateG_m2_d}</div>
                  <div>α:</div><div className="text-slate-200 font-mono">{p.alphaFactor}</div>
                </div>
              </button>
            ))}
          </div>
          <p className="text-[10px] text-rb-brand mt-2">
            Active: <strong>{profile.label}</strong> — {profile.desc}
          </p>
        </div>

        {/* ─── HERO PASS/FAIL SUMMARY ───────────────────────────────────── */}
        <div className={`rb-card p-5 border-2 ${
          overallStatus === 'pass' ? 'border-emerald-500/40 bg-emerald-500/5'
          : overallStatus === 'warn' ? 'border-amber-500/40 bg-amber-500/5'
          : 'border-red-500/40 bg-red-500/5'
        }`}>
          <div className="flex items-start gap-4">
            <StatusIcon s={overallStatus} />
            <div className="flex-1">
              <h2 className="text-base font-bold text-slate-100">
                Overall Validation: {overallPasses} / {overallTotal} checks passed
              </h2>
              <p className="text-[11px] text-slate-400 mt-1">
                {overallStatus === 'pass'
                  ? 'All independent physics checks and design optimisation criteria are satisfied. Layout is engineering-validated.'
                  : overallStatus === 'warn'
                  ? `${overallTotal - overallPasses} non-critical warnings — review highlighted sections.`
                  : `${overallTotal - overallPasses} critical failures — design must be revised.`}
              </p>
              <p className="text-[10px] text-slate-500 mt-2 font-mono">
                Recommended: <strong className="text-rb-brand">{OPT.rec} modules</strong> at{' '}
                <strong className="text-rb-brand">{fmt(OPT.recSpacing, 1)} m</strong> spacing
                · {fmt(OPT.totalAirflow, 0)} Nm³/h · {fmt(OPT.totalAOTR, 2)} kg O₂/h · {fmt(OPT.totalKw, 1)} kW
              </p>
            </div>
          </div>
        </div>

        {/* ─── §1 SURVEY DATA ───────────────────────────────────────────── */}
        <Section>
          <SectionHeader
            icon={MapPin} color="bg-blue-500/20 text-blue-300" num={1}
            title="Survey Data Validation"
            subtitle="Source: Shoreline Surveys Ltd, J2020 (Jan 2023)"
            status={checks.surveyDataPresent ? 'pass' : 'fail'}
          />
          <table className="w-full text-xs">
            <tbody>
              <Row label="Survey provider"      value="Shoreline Surveys Ltd"            unit="" status="pass" />
              <Row label="Project number"       value="J2020"                            unit="" status="pass" />
              <Row label="Drawing"              value="01v2"                             unit="" status="pass" />
              <Row label="Survey date"          value="09 January 2023"                  unit="" status="pass" />
              <Row label="Client"               value="Atkins Réalis"                     unit="" status="pass" />
              <Row label="Survey method"        value="Single-beam sonar"                unit="" status="pass" />
              <Row label="Paper scale"          value="1:1,000 on A1"                    unit="" status="pass" />
              <Row label="Datum"                value="ODN Newlyn"                       unit="" status="pass" />
              <Row label="Coordinate system"    value="OSGB36 OSTN15"                    unit="" status="pass" />
              <Row label="Water level"          value={`${WATER_LEVEL_ODN}`}             unit="m ODN" status="pass" />
              <Row label="Depth range surveyed" value="0.79 – 4.79"                      unit="m" bench="positive depth" status="pass" />
              <Row label="Depth contour interval" value="0.25"                           unit="m" status="pass" />
              <Row label="Grid easting"         value="≈ 543,750 – 544,000"              unit="E" status="pass" />
              <Row label="Grid northing"        value="≈ 115,000"                        unit="N" status="pass" />
              <Row label="Survey context"       value="Pre-dredge condition"             unit="" status="info" />
            </tbody>
          </table>
        </Section>

        {/* ─── §2 RESERVOIR GEOMETRY ────────────────────────────────────── */}
        <Section>
          <SectionHeader
            icon={Ruler} color="bg-indigo-500/20 text-indigo-300" num={2}
            title="Reservoir Geometry — Area, Volume, Depth Distribution"
            subtitle="Digitised polygon (57 vertices); shoelace area formula + cell-based sampling"
            status={checks.reservoirAreaPlaus && checks.meanDepthPlaus ? 'pass' : 'warn'}
          />
          <Formula>
            Shoelace area: A = ½ |Σ (x_i · y_{'{i+1}'} − x_{'{i+1}'} · y_i)|<br />
            Volume: V = Σ d(x,y) · ΔA  (cell sampling at Δ = 3 m){'  '}<br />
            Mean depth: H̄ = V / A
          </Formula>
          <table className="w-full text-xs">
            <tbody>
              <Row label="Polygon vertices"  value={`${RESERVOIR_POLY.length}`}    unit="" status="pass" />
              <Row label="Plan area"         formula="shoelace"     value={fmt(RES.areaHa, 3)}    unit="ha"
                bench="2 – 8 ha (UK small reservoir)" status={checks.reservoirAreaPlaus ? 'pass' : 'warn'} />
              <Row label="Plan area (m²)"    value={fmt(RES.areaM2, 0)} unit="m²" status="pass" />
              <Row label="Mean depth"        formula="V/A"          value={fmt(RES.meanDepth, 2)} unit="m"
                bench="1.5 – 5.0 m (shallow)" status={checks.meanDepthPlaus ? 'pass' : 'warn'} />
              <Row label="Estimated volume"  formula="Σ d·ΔA"       value={fmt(RES.volumeM3, 0)} unit="m³" status="pass" />
              <Row label="Volume per ha"     value={fmt(RES.volumeM3 / RES.areaHa, 0)} unit="m³/ha"
                bench="15,000 – 35,000" status="info" />
              <Row label="Area < 2 m deep"   value={fmt(RES.shallow_m2 / 10000, 2)} unit="ha"
                bench={`${fmt(RES.shallow_m2 / RES.areaM2 * 100, 0)}% of total`} status="info" />
              <Row label="Area 2 – 3 m"      value={fmt(RES.medium_m2 / 10000, 2)} unit="ha"
                bench={`${fmt(RES.medium_m2 / RES.areaM2 * 100, 0)}% (dominant)`} status="info" />
              <Row label="Area 3 – 4 m"      value={fmt(RES.deep_m2 / 10000, 2)} unit="ha"
                bench={`${fmt(RES.deep_m2 / RES.areaM2 * 100, 0)}%`} status="info" />
              <Row label="Area &gt; 4 m"     value={fmt(RES.deepest_m2 / 10000, 2)} unit="ha"
                bench={`${fmt(RES.deepest_m2 / RES.areaM2 * 100, 0)}% (NE basin)`} status="info" />
              <Row label="Effective area (≥ 2 m)" value={fmt((RES.medium_m2 + RES.deep_m2 + RES.deepest_m2) / 10000, 2)} unit="ha"
                bench="for fine-bubble aeration"
                status={(RES.medium_m2 + RES.deep_m2 + RES.deepest_m2) > 0.5 * RES.areaM2 ? 'pass' : 'warn'} />
            </tbody>
          </table>
        </Section>

        {/* ─── §3 MODULE DESIGN SUMMARY ─────────────────────────────────── */}
        <Section>
          <SectionHeader
            icon={Layers} color="bg-purple-500/20 text-purple-300" num={3}
            title="Module Design Summary"
            subtitle="Input parameters from sessionStorage (rb_diffuser_module_v1)"
            status="info"
          />
          <table className="w-full text-xs">
            <tbody>
              <Row label="Frame size"        value={`${FRAME_SIZE} × ${FRAME_SIZE}`}        unit="m" status="info" />
              <Row label="Diffuser type"     value={spec.diffuserType}                       unit="" status="info" />
              <Row label="Diffusers/module"  value={`${spec.diffuserCount}`}                 unit=""
                bench="4 – 12 typical" status={spec.diffuserCount >= 4 && spec.diffuserCount <= 12 ? 'pass' : 'warn'} />
              <Row label="Corner offset"     value={fmt(spec.cornerOffsetM, 2)}              unit="m"
                bench="0.15 – 0.50 m" status="pass" />
              <Row label="Mounting height"   value={fmt(spec.mountingHeightM, 2)}            unit="m"
                bench="0.30 – 0.60 m" status={checks.mountingHeight ? 'pass' : 'warn'} />
              <Row label="Diffuser diameter" value={fmt(spec.diffuserDiamMm, 0)}             unit="mm"
                bench="220 – 350 mm" status="pass" />
              <Row label="Pore size"         value={fmt(spec.poreSizeUm, 0)}                 unit="μm"
                bench="40 – 300 μm" status={checks.poreSize ? 'pass' : 'warn'} />
              <Row label="Pores per diffuser" value={`${V.poresEach}`}                       unit="" status="pass" />
              <Row label="Total pores/module" value={`${V.poresTotal}`}                      unit="" status="pass" />
              <Row label="Airflow per diff"  value={fmt(spec.airflowPerDiffNm3h, 2)}         unit="Nm³/h"
                bench="0.5 – 3.0 Nm³/h" status="pass" />
              <Row label="Total airflow/mod" value={fmt(V.qTotal, 2)}                        unit="Nm³/h" status="pass" />
              <Row label="Water depth (design)" value={fmt(spec.waterDepthM, 2)}             unit="m" status="info" />
              <Row label="Water temperature" value={fmt(spec.waterTempC, 1)}                 unit="°C"
                bench="UK reservoirs: 4 – 22 °C" status="pass" />
              <Row label="Current DO"        value={fmt(spec.currentDoMgL, 2)}               unit="mg/L"
                bench="Pre-dredge: 1 – 4 mg/L" status="pass" />
              <Row label="α factor"          value={fmt(spec.alphaFactor, 2)}                unit=""
                bench="0.6 – 0.9 (freshwater)" status={checks.alphaFactor ? 'pass' : 'warn'} />
              <Row label="β factor"          value={fmt(spec.betaFactor, 2)}                 unit=""
                bench="0.95 – 1.0" status="pass" />
              <Row label="Blower efficiency" value={fmt(spec.blowerEff, 2)}                  unit=""
                bench="0.55 – 0.75 (positive disp.)" status="pass" />
            </tbody>
          </table>
        </Section>

        {/* ─── §4 WATER PROPERTIES ──────────────────────────────────────── */}
        <Section>
          <SectionHeader
            icon={FlaskConical} color="bg-cyan-500/20 text-cyan-300" num={4}
            title="Water Physical Properties"
            subtitle={`Kell (1975) density · Andrade-Vogel viscosity · Vargaftik (1983) surface tension @ ${V.T} °C`}
            status="pass"
          />
          <table className="w-full text-xs">
            <tbody>
              <Row label="Density ρ_w"           formula="Kell (1975) poly"   value={fmt(V.rho, 3)}             unit="kg/m³"
                bench="999.5 – 1000.0" status="pass" />
              <Row label="Dynamic viscosity μ"   formula="Andrade-Vogel"      value={(V.mu * 1000).toFixed(3)}  unit="mPa·s"
                bench="0.89 (20 °C) – 1.78 (0 °C)" status="pass" />
              <Row label="Kinematic viscosity ν" formula="μ/ρ_w"              value={(V.nu * 1e6).toFixed(3)}   unit="mm²/s"
                bench="0.89 – 1.78" status="pass" />
              <Row label="Surface tension σ"     formula="Vargaftik (1983)"   value={(V.sigma * 1000).toFixed(2)} unit="mN/m"
                bench="71.0 – 75.6" status="pass" />
              <Row label="DO saturation"         formula="Benson & Krause"    value={fmt(V.dosat, 2)}           unit="mg/L"
                bench="8.0 – 14.0 mg/L (4 – 22 °C)" status="pass" />
            </tbody>
          </table>
        </Section>

        {/* ─── §5 BUBBLE PHYSICS ────────────────────────────────────────── */}
        <Section>
          <SectionHeader
            icon={Wind} color="bg-sky-500/20 text-sky-300" num={5}
            title="Per-Module Bubble Physics"
            subtitle="Fritz (1935) + Loubière & Hébrard (2003) bubble formation; Clift et al. (1978) rise velocity"
            status={checks.bubblePhysics && checks.riseVel ? 'pass' : 'warn'}
          />
          <Formula>
            d_stat = [6 σ d_pore / (ρ_w g)]^(1/3)<br />
            d_b = d_stat × (1 + 0.9 × (q_pore × 10⁹)^0.25) × 1000  [mm]<br />
            v_rise = v₀ × (ν_20 / ν_T)^0.7
          </Formula>
          <table className="w-full text-xs">
            <tbody>
              <Row label="Airflow per pore"      formula="q_total / N_pores" value={(V.qTotal / 3600 / V.poresTotal * 1e9).toFixed(2)} unit="μL/s/pore"
                bench="2 – 100 μL/s/pore" status="pass" />
              <Row label="Static bubble diam"    formula="Fritz (1935)"      value={fmt(Math.pow((6 * V.sigma * spec.poreSizeUm / 1e6) / (V.rho * G), 1/3) * 1000, 3)} unit="mm"
                bench="0.5 – 3.0 mm" status="pass" />
              <Row label="Bubble diameter"       formula="Loubière (2003)"   value={fmt(V.d_b, 3)}                unit="mm"
                bench="1 – 5 mm (fine-bubble)" status={checks.bubblePhysics ? 'pass' : 'warn'} />
              <Row label="Rise velocity"         formula="Clift (1978)"      value={fmt(V.riseV, 3)}              unit="m/s"
                bench="0.10 – 0.40 m/s" status={checks.riseVel ? 'pass' : 'warn'} />
              <Row label="Effective submergence" formula="H − h_mount"       value={fmt(V.effSubm, 2)}            unit="m"
                bench="≥ 1.5 m for fine-bubble" status={checks.submergence ? 'pass' : 'warn'} />
              <Row label="Bubble residence time" formula="H_eff / v_rise"    value={fmt(V.effSubm / V.riseV, 1)}  unit="s"
                bench="5 – 60 s" status="pass" />
              <Row label="Bed influence radius"  formula="max(r_diff, 0.35·H)" value={fmt(V.r_inf_bed, 2)}         unit="m"
                bench="Seol (2007)" status="pass" />
              <Row label="Surface plume radius"  formula="r_diff + 0.1·H"    value={fmt(V.r_surface, 2)}          unit="m"
                bench="5.7° cone half-angle" status="pass" />
              <Row label="Module bed radius"     formula="L/√2 + r_inf"      value={fmt(V.r_module, 2)}           unit="m"
                bench="combined frame coverage" status="pass" />
            </tbody>
          </table>
        </Section>

        {/* ─── §6 O₂ TRANSFER ───────────────────────────────────────────── */}
        <Section>
          <SectionHeader
            icon={Activity} color="bg-emerald-500/20 text-emerald-300" num={6}
            title="Per-Module Oxygen Transfer"
            subtitle="Stenstrom & Rosso (2008) SOTE · ASCE 2-06 SOTR/AOTR · Metcalf & Eddy (2014) α/β corrections"
            status={checks.soteOk && checks.aotrPositive ? 'pass' : 'warn'}
          />
          <Formula>
            SOTE/m = 10.5 × exp(−0.32 × d_b)  [%/m]<br />
            SOTR = q · ρ_air · w_O₂ · (SOTE × H_eff / 100)<br />
            AOTR = SOTR · α · β · 1.024^(T−20) · (C_sat − C_L) / C_sat,20
          </Formula>
          <table className="w-full text-xs">
            <tbody>
              <Row label="SOTE per metre"        formula="Stenstrom (2008)"  value={fmt(V.soteM, 2)}               unit="%/m"
                bench="2 – 9 %/m" status={checks.soteOk ? 'pass' : 'warn'} />
              <Row label="Total SOTE"            formula="SOTE/m × H_eff"    value={fmt(V.sote_total, 2)}          unit="%"
                bench="5 – 30 %" status="pass" />
              <Row label="SOTR per module"       formula="ASCE 2-06"         value={fmt(V.sotr, 4)}                unit="kg O₂/h"
                bench="0.1 – 2.0 kg O₂/h" status="pass" />
              <Row label="AOTR per module"       formula="α·β·θ·ΔDO"         value={fmt(V.aotr, 4)}                unit="kg O₂/h"
                bench="0.05 – 1.5 kg O₂/h" status={checks.aotrPositive ? 'pass' : 'warn'} />
              <Row label="Blower power/module"   formula="isentropic"        value={fmt(V.kw, 3)}                  unit="kW"
                bench="0.5 – 5 kW/module" status={checks.kwPositive ? 'pass' : 'warn'} />
              <Row label="Aeration efficiency"   formula="AOTR / kW"         value={fmt(V.SAE, 2)}                 unit="kg O₂/kWh"
                bench="≥ 1.2 (good)" status={checks.saeOk ? 'pass' : 'warn'} />
            </tbody>
          </table>
        </Section>

        {/* ─── §7 SOD DEMAND & O₂ BALANCE ───────────────────────────────── */}
        <Section>
          <SectionHeader
            icon={BarChart3} color="bg-amber-500/20 text-amber-300" num={7}
            title="Sediment Oxygen Demand &amp; O₂ Balance"
            subtitle={`Hickey & Gibbs (2009) typical SOD = 0.5–3.0 g O₂/m²/d; using ${sodRate} g O₂/m²/d for Barcombe pre-dredge`}
            status={checks.sodSupplyMet ? (checks.sodSupplyWithSafety ? 'pass' : 'warn') : 'fail'}
          />
          <Formula>
            SOD_total = A_reservoir × SOD_rate / (1000 × 24)  [kg O₂/h]<br />
            n_required = ⌈ (SOD_total × safety) / AOTR_module ⌉
          </Formula>
          <table className="w-full text-xs">
            <tbody>
              <Row label="Assumed SOD rate"      value={fmt(sodRate, 2)}                       unit="g O₂/m²/d"
                bench="Hickey & Gibbs: 0.5 – 3.0" status="info" />
              <Row label="Total SOD demand"      formula="A × rate"          value={fmt(OPT.totalSOD, 3)}          unit="kg O₂/h" status="info" />
              <Row label="Daily SOD"             value={fmt(OPT.totalSOD * 24, 2)}             unit="kg O₂/d" status="info" />
              <Row label="Safety factor"         value={fmt(safetyF, 2)}                       unit="× "
                bench="1.2 – 2.0 typical" status="info" />
              <Row label="SOD demand with safety" value={fmt(OPT.sodWithSafety, 3)}            unit="kg O₂/h" status="info" />
              <Row label="Modules to meet SOD"   formula="⌈SOD·sf / AOTR⌉"   value={`${OPT.modsBySOD}`}            unit=""
                status="pass" />
              <Row label="Recommended total AOTR" value={fmt(OPT.totalAOTR, 3)}                unit="kg O₂/h" status="pass" />
              <Row label="O₂ surplus"            formula="AOTR − SOD"        value={fmt(OPT.surplus, 3)}           unit="kg O₂/h"
                bench={`> 0 (supply ≥ demand): ${OPT.surplus >= 0 ? 'YES' : 'NO'}`}
                status={checks.sodSupplyMet ? (checks.sodSupplyWithSafety ? 'pass' : 'warn') : 'fail'} />
              <Row label="Supply ratio"          value={fmt(OPT.totalAOTR / OPT.totalSOD, 2)} unit="×"
                bench="≥ 1.3 with safety" status={(OPT.totalAOTR / OPT.totalSOD) >= safetyF ? 'pass' : 'warn'} />
            </tbody>
          </table>
        </Section>

        {/* ─── §8 DESTRATIFICATION COVERAGE ─────────────────────────────── */}
        <Section>
          <SectionHeader
            icon={Target} color="bg-teal-500/20 text-teal-300" num={8}
            title="Destratification Coverage Analysis"
            subtitle="Wüest et al. (1992) mixing radius · Lemckert & Imberger (1993) plume extent"
            status={checks.coverageMet ? 'pass' : 'warn'}
          />
          <Formula>
            r_mix = k × H̄  (k = 5, Wüest 1992 for typical bubble plumes)<br />
            A_per_module = π · r_mix² · η  (η = 0.65, hexagonal-packing efficiency w/ edge losses)<br />
            n_required = ⌈ (target_cov × A_eff) / A_per_module ⌉
          </Formula>
          <table className="w-full text-xs">
            <tbody>
              <Row label="Mean depth H̄"          value={fmt(RES.meanDepth, 2)}                unit="m" status="pass" />
              <Row label="Mixing radius r_mix"    formula="5 × H̄"             value={fmt(OPT.rMix, 1)}             unit="m"
                bench="4–6 × H (Wüest 1992)" status="pass" />
              <Row label="Area per module"        formula="π·r²·0.65"        value={fmt(OPT.areaPerMod, 0)}        unit="m²" status="pass" />
              <Row label="Effective area (≥2 m)"  value={fmt(OPT.effArea, 0)}                  unit="m²"
                bench={`${fmt(OPT.effArea / RES.areaM2 * 100, 0)}% of reservoir`} status="info" />
              <Row label="Target coverage"        value={`${target}`}                          unit="%"
                bench="AWWA: 60–80%" status="pass" />
              <Row label="Modules for coverage"   formula="⌈target·A_eff/A_mod⌉" value={`${OPT.modsByCov}`}        unit="" status="pass" />
              <Row label="Expected coverage"      formula="n·A_mod/A_eff"     value={fmt(OPT.expectedCov, 1)}      unit="%"
                bench={`≥ ${target - 5}%`} status={checks.coverageMet ? 'pass' : 'warn'} />
            </tbody>
          </table>
        </Section>

        {/* ─── §9 OPTIMAL MODULE COUNT ──────────────────────────────────── */}
        <Section>
          <SectionHeader
            icon={Sparkles} color="bg-rose-500/20 text-rose-300" num={9}
            title="Optimal Module Count Derivation"
            subtitle="Recommendation = max(SOD-required, coverage-required) — most binding constraint"
            status={checks.modulesPerHaPlaus ? 'pass' : 'warn'}
          />
          <table className="w-full text-xs">
            <tbody>
              <Row label="By SOD demand"          value={`${OPT.modsBySOD}`}                   unit="modules"
                bench="oxygen-supply constraint" status="info" />
              <Row label="By coverage requirement" value={`${OPT.modsByCov}`}                  unit="modules"
                bench="destratification constraint" status="info" />
              <Row label="Recommended count"      formula="max()"             value={`${OPT.rec}`}                  unit="modules"
                bench={OPT.modsBySOD >= OPT.modsByCov ? 'SOD-limited' : 'coverage-limited'}
                status="pass" />
              <Row label="Modules per hectare"    formula="n / A_ha"          value={fmt(OPT.rec / RES.areaHa, 2)} unit="/ha"
                bench="AWWA: 1–4 /ha (typical 2.5)" status={checks.modulesPerHaPlaus ? 'pass' : 'warn'} />
              <Row label="Total airflow"          value={fmt(OPT.totalAirflow, 1)}             unit="Nm³/h" status="pass" />
              <Row label="Total blower power"     value={fmt(OPT.totalKw, 2)}                  unit="kW"
                bench="≈ 0.5–3 kW/ha" status="pass" />
              <Row label="Specific energy"        formula="kW / ha"           value={fmt(OPT.totalKw / RES.areaHa, 2)} unit="kW/ha"
                bench="0.5 – 5 kW/ha typical" status="pass" />
            </tbody>
          </table>
        </Section>

        {/* ─── §10 OPTIMAL SPACING ──────────────────────────────────────── */}
        <Section>
          <SectionHeader
            icon={Ruler} color="bg-orange-500/20 text-orange-300" num={10}
            title="Optimal Spacing Derivation"
            subtitle="Schladow (1993) plume spacing methodology; bounded by merge / coverage constraints"
            status={checks.spacingMin && checks.spacingMax ? 'pass' : 'warn'}
          />
          <Formula>
            s_square = √(A_eff / n)<br />
            s_hex    = s_square × √(2/√3) ≈ s_square × 1.0746<br />
            s_min    = 2 × r_plume_surface  (avoid plume merge)<br />
            s_max    = 2 × r_mix            (continuous coverage)
          </Formula>
          <table className="w-full text-xs">
            <tbody>
              <Row label="Square-grid spacing"    formula="√(A/n)"            value={fmt(OPT.recSpacing, 2)}        unit="m" status="pass" />
              <Row label="Hex-grid spacing"       formula="× 1.0746"          value={fmt(OPT.recSpacing * 1.0746, 2)} unit="m"
                bench="7.5% more efficient" status="pass" />
              <Row label="Minimum spacing"        formula="2·r_surface"       value={fmt(OPT.minSpacing, 2)}         unit="m"
                bench="avoid merge" status="pass" />
              <Row label="Maximum spacing"        formula="2·r_mix"           value={fmt(OPT.maxSpacing, 2)}         unit="m"
                bench="continuous coverage" status="pass" />
              <Row label="Recommended in bounds"  value={`${OPT.recSpacing.toFixed(1)} ∈ [${OPT.minSpacing.toFixed(0)}, ${OPT.maxSpacing.toFixed(0)}]`} unit="m"
                status={checks.spacingMin && checks.spacingMax ? 'pass' : 'warn'} />
              <Row label="Plume merge check"      value={OPT.recSpacing >= OPT.minSpacing ? 'CLEAR' : 'FAIL'} unit=""
                bench="spacing ≥ 2·r_surface" status={checks.spacingMin ? 'pass' : 'fail'} />
              <Row label="Coverage continuity"    value={OPT.recSpacing <= OPT.maxSpacing ? 'CONTINUOUS' : 'GAPS'} unit=""
                bench="spacing ≤ 2·r_mix" status={checks.spacingMax ? 'pass' : 'warn'} />
            </tbody>
          </table>
        </Section>

        {/* ─── §11 PLACEMENT VALIDATION ─────────────────────────────────── */}
        <Section>
          <SectionHeader
            icon={Shield} color="bg-violet-500/20 text-violet-300" num={11}
            title="Placement Zone Validation"
            subtitle="Verification that recommended modules can be placed in suitable depth zones"
            status={
              OPT.rec <= Math.floor((RES.medium_m2 + RES.deep_m2 + RES.deepest_m2) / 100)
                ? 'pass' : 'warn'
            }
          />
          <table className="w-full text-xs">
            <tbody>
              <Row label="Available area ≥ 2m"   value={fmt(OPT.effArea / 10000, 2)} unit="ha" status="pass" />
              <Row label="Min spacing footprint"  value={fmt(OPT.recSpacing ** 2, 0)} unit="m²/module" status="pass" />
              <Row label="Max modules placeable"  formula="A_eff/s²"          value={`${Math.floor(OPT.effArea / OPT.recSpacing ** 2)}`} unit=""
                bench={`≥ ${OPT.rec} required`}
                status={Math.floor(OPT.effArea / OPT.recSpacing ** 2) >= OPT.rec ? 'pass' : 'warn'} />
              <Row label="Deep-zone modules (3–4m)" value={`≤ ${Math.floor(RES.deep_m2 / OPT.recSpacing ** 2)}`} unit="placeable" status="info" />
              <Row label="Deepest-zone modules (>4m)" value={`≤ ${Math.floor(RES.deepest_m2 / OPT.recSpacing ** 2)}`} unit="placeable" status="info" />
              <Row label="Pre-dredge zone modules (2–3m)" value={`≤ ${Math.floor(RES.medium_m2 / OPT.recSpacing ** 2)}`} unit="placeable" status="info" />
              <Row label="Placement priority"    value="Deep → medium-deep → silt zone" unit=""
                bench="maximise per-module SOTE" status="info" />
            </tbody>
          </table>
        </Section>

        {/* ─── §12 FINAL PASS/FAIL SUMMARY ──────────────────────────────── */}
        <Section>
          <SectionHeader
            icon={CheckCircle2} color="bg-emerald-500/20 text-emerald-300" num={12}
            title="Final Pass/Fail Summary"
            subtitle={`${overallPasses} of ${overallTotal} validation checks passed`}
            status={overallStatus}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[
              { k: 'surveyDataPresent',    lbl: 'Survey data complete & validated' },
              { k: 'reservoirAreaPlaus',   lbl: 'Reservoir area within plausible range (1–20 ha)' },
              { k: 'meanDepthPlaus',       lbl: 'Mean depth plausible (1.5–5.0 m)' },
              { k: 'bubblePhysics',        lbl: 'Bubble diameter 1–5 mm (fine-bubble regime)' },
              { k: 'riseVel',              lbl: 'Bubble rise velocity 0.10–0.40 m/s' },
              { k: 'submergence',          lbl: 'Effective submergence ≥ 1.5 m' },
              { k: 'soteOk',               lbl: 'SOTE per metre ≥ 2.0 %' },
              { k: 'aotrPositive',         lbl: 'Per-module AOTR > 0.01 kg O₂/h' },
              { k: 'kwPositive',           lbl: 'Per-module blower power 0–50 kW' },
              { k: 'saeOk',                lbl: 'Aeration efficiency ≥ 0.5 kg O₂/kWh' },
              { k: 'sodSupplyMet',         lbl: 'Total AOTR meets total SOD demand' },
              { k: 'sodSupplyWithSafety',  lbl: 'Total AOTR meets SOD × safety factor' },
              { k: 'coverageMet',          lbl: `Coverage ≥ ${target - 5}% (within tolerance)` },
              { k: 'modulesPerHaPlaus',    lbl: 'Modules/ha plausible (1–20, AWWA range)' },
              { k: 'spacingMin',           lbl: 'Spacing ≥ minimum (no plume merge)' },
              { k: 'spacingMax',           lbl: 'Spacing ≤ maximum (continuous coverage)' },
              { k: 'poreSize',             lbl: 'Pore size 40–300 μm' },
              { k: 'mountingHeight',       lbl: 'Mounting height 0.30–0.60 m' },
              { k: 'alphaFactor',          lbl: 'α factor in plausible range (0.6–1.0)' },
            ].map(c => {
              const passed = checks[c.k as keyof typeof checks];
              return (
                <div key={c.k} className={`flex items-center gap-2 p-2 rounded-lg ${
                  passed ? 'bg-emerald-500/5 border border-emerald-500/15' : 'bg-amber-500/5 border border-amber-500/15'
                }`}>
                  <StatusIcon s={passed ? 'pass' : 'warn'} />
                  <span className="text-[11px] text-slate-300 flex-1">{c.lbl}</span>
                </div>
              );
            })}
          </div>
        </Section>

        {/* ─── §13 REFERENCES ───────────────────────────────────────────── */}
        <Section>
          <SectionHeader
            icon={BookOpen} color="bg-slate-500/20 text-slate-300" num={13}
            title="Literature References"
            subtitle="Peer-reviewed sources cited throughout this validation"
            status="info"
          />
          <ol className="space-y-2 text-[10px] text-slate-400 list-decimal list-inside leading-relaxed">
            <li><strong className="text-slate-200">Shoreline Surveys Ltd (2023).</strong> High frequency single beam bathymetric survey of Barcombe Reservoir, Sussex. Project J2020 Drawing 01v2.</li>
            <li><strong className="text-slate-200">Fritz, W. (1935).</strong> Berechnung des Maximalvolumens von Dampfblasen. Physik. Zeitschrift 36, 379–384.</li>
            <li><strong className="text-slate-200">Clift, R., Grace, J.R. &amp; Weber, M.E. (1978).</strong> Bubbles, drops and particles. Academic Press.</li>
            <li><strong className="text-slate-200">Loubière, K. &amp; Hébrard, G. (2003).</strong> Bubble formation at a rigid and flexible orifice in viscous liquids. Chem. Eng. Process. 42(11), 875–886.</li>
            <li><strong className="text-slate-200">Stenstrom, M.K. &amp; Rosso, D. (2008).</strong> A review of oxygen transfer standards, models, monitoring and control. ASCE MOP 36.</li>
            <li><strong className="text-slate-200">ASCE (2006).</strong> Measurement of oxygen transfer in clean water. ASCE/EWRI 2-06.</li>
            <li><strong className="text-slate-200">Metcalf &amp; Eddy / Tchobanoglous, G. et al. (2014).</strong> Wastewater Engineering: Treatment &amp; Resource Recovery, 5th edn. McGraw-Hill.</li>
            <li><strong className="text-slate-200">Seol, D.G., Bhaumik, T., Bergmann, C. &amp; Bhate, R.K. (2007).</strong> PIV measurements of the flow field induced by a submerged bubble plume. J. Hydraul. Eng. 133(6), 627–640.</li>
            <li><strong className="text-slate-200">Schladow, S.G. (1993).</strong> Lake destratification by bubble-plume systems: design methodology. J. Hydraul. Eng. 119(3), 350–368.</li>
            <li><strong className="text-slate-200">Wüest, A., Brooks, N.H. &amp; Imboden, D.M. (1992).</strong> Bubble plume modeling for lake restoration. Water Resour. Res. 28(12), 3235–3250.</li>
            <li><strong className="text-slate-200">Lemckert, C.J. &amp; Imberger, J. (1993).</strong> Energetic bubble plumes in arbitrary stratification. J. Hydraul. Eng. 119(6), 680–703.</li>
            <li><strong className="text-slate-200">Hickey, C.W. &amp; Gibbs, M.M. (2009).</strong> Lake sediment oxygen demand and nutrient dynamics. N.Z. J. Mar. Freshwater Res. 43(2), 693–714.</li>
            <li><strong className="text-slate-200">AWWA (2011).</strong> Operational Control of Coagulation and Filtration Processes. AWWA Manual M37, 3rd edn.</li>
            <li><strong className="text-slate-200">Singleton, V.L. &amp; Little, J.C. (2006).</strong> Designing hypolimnetic aeration and oxygenation systems — a review. Environ. Sci. Technol. 40(24), 7512–7520.</li>
            <li><strong className="text-slate-200">Shields, A. (1936).</strong> Anwendung der Ähnlichkeitsmechanik und der Turbulenzforschung auf die Geschiebebewegung. Mitteilungen Preuss. VWS, Heft 26.</li>
            <li><strong className="text-slate-200">van Rijn, L.C. (1984).</strong> Sediment transport, part I: bed load transport. J. Hydraul. Eng. 110(10), 1431–1456.</li>
            <li><strong className="text-slate-200">Kell, G.S. (1975).</strong> Density, thermal expansivity, and compressibility of liquid water 0°–150°C. J. Chem. Eng. Data 20(1), 97–105.</li>
            <li><strong className="text-slate-200">Vargaftik, N.B., Volkov, B.N. &amp; Voljak, L.D. (1983).</strong> International tables of the surface tension of water. J. Phys. Chem. Ref. Data 12(3), 817–820.</li>
          </ol>
        </Section>

        {/* Footer / report metadata */}
        <div className="text-center text-[10px] text-slate-600 pt-4 pb-8 print:pt-2 print:pb-2">
          <div>Reynolds &amp; Bauhm Smart Water · Reservoir Validation Report · Barcombe Reservoir</div>
          <div className="mt-1">Generated {new Date().toLocaleString('en-GB')}</div>
        </div>
      </div>
    </div>
  );
}

